import { ReactElement, ReactNode, useRef } from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { getScreenFromPathname, mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { cn } from "@/lib/utils";

// Wraps children in a design-system tooltip only when there is text
// text to show. Otherwise renders the children untouched. Children
// must be a single element (not a string/fragment) that forwards
// refs — Radix's asChild slot requires both.
export const OptionalTooltip = ({
  children,
  contentClassName,
  text,
  trackingLabel = "Tooltip",
}: {
  children: ReactElement;
  contentClassName?: string;
  text?: ReactNode;
  trackingLabel?: string;
}) => {
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  if (!text) {
    return children;
  }

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger
          asChild
          onMouseEnter={() => {
            hoverTimerRef.current = setTimeout(() => {
              mixpanelUtils.trackHover(
                trackingLabel,
                getScreenFromPathname(window.location.pathname),
              );
            }, 300);
          }}
          onMouseLeave={() => {
            if (hoverTimerRef.current) {
              clearTimeout(hoverTimerRef.current);
              hoverTimerRef.current = null;
            }
          }}
        >
          {children}
        </TooltipTrigger>
        <TooltipContent
          align="start"
          // Keep a gutter between the tooltip and the viewport edge when
          // Radix's collision handling shifts it (default padding is 0,
          // which parks edge-adjacent tooltips flush against the window).
          collisionPadding={12}
          className={cn("max-w-xl whitespace-pre-wrap", contentClassName)}
        >
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
